import {
  type AxiosError,
  type AxiosInstance,
  HttpStatusCode,
  isAxiosError,
} from "axios";
import { type ReuseInfoGetAPIResponse } from "@/features/acquireBook/api/ReuseInfoAPI";
import { type BookTypeEng } from "@/types/acquirebook";
import useSWRMutation, { type SWRMutationResponse } from "swr/mutation";
import { SendToTrackingService } from "@/api/handlers/errors";

export interface AddressSetForReuse {
  prefName: string;
  locationFull: string;
  chibanKaokuNumber: string;
}

export interface ReusePicturesAPIRequest {
  land: AddressSetForReuse;
  building: AddressSetForReuse;
}

export interface PropertySheetReuseInfo {
  realEstateType: BookTypeEng;
  prefName: string;
  locationFull: string;
  chibanKaokuNumber: string;
  reusePictures: ReuseInfoGetAPIResponse[];
}

export interface ReusePicturesGetAPIResponse extends AddressSetForReuse {
  reuseLand: PropertySheetReuseInfo;
  reuseBuilding: PropertySheetReuseInfo;
}

export const useReusePicturesMutateAPI = (
  apiClient: AxiosInstance
): SWRMutationResponse<
  ReusePicturesGetAPIResponse | null,
  never,
  string,
  ReusePicturesAPIRequest
> => {
  const fetcher = async (
    url: string,
    { arg }: { arg: ReusePicturesAPIRequest }
  ): Promise<ReusePicturesGetAPIResponse | null> => {
    const queryParams = new URLSearchParams({
      building_chiban_kaoku_number: arg.building.chibanKaokuNumber,
      building_location_full: arg.building.locationFull,
      building_pref_name: arg.building.prefName,
      land_chiban_kaoku_number: arg.land.chibanKaokuNumber,
      land_location_full: arg.land.locationFull,
      land_pref_name: arg.land.prefName,
    });

    try {
      const response = await apiClient.get<ReusePicturesGetAPIResponse>(
        `${url}?${queryParams.toString()}`
      );
      return response.data;
    } catch (error: unknown) {
      if (
        isAxiosError(error) &&
        ((error as AxiosError).response?.status ??
          HttpStatusCode.InternalServerError) >=
          HttpStatusCode.InternalServerError
      ) {
        SendToTrackingService(error as AxiosError<unknown>);
      }
      return null;
    }
  };

  return useSWRMutation(
    "/picture-request/acquire-reuse/land-building/all-matters",
    fetcher,
    {
      revalidate: false,
    }
  );
};
